/// <reference lib="dom" />
/**
 * webFinaleDraw.web.ts
 * Canvas2D draw helpers for the Level 1-1 finale cinematic sequence.
 *
 * Reads from the module-level `finaleState` export in finaleState.ts.
 * All functions are stateless reads — they never mutate gameState or finaleState.
 *
 * Web-only (.web.ts) — never bundled on native.
 *
 * Phase 3 — Art, Audio & Polish.
 *
 * @module webFinaleDraw
 */

import { finaleState } from '../scenes/finaleState';
import {
  NOVA_SHIP_SHEET,
  FLAME_IDLE_SHEET,
  FLAME_CRUISE_SHEET,
  FLAME_BOOST_SHEET,
  FLAME_CLIP,
  FLAME_ANCHOR,
  advanceAnim,
  type FlameMode,
} from './SpriteRenderer';
import type { AnimState } from './webSpriteDraw';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { drawRamp, drawDust, drawSonicBoom } from './webFinaleEffects';

// ── Flame animation state (module-level, persists across frames) ──────────

const _flameAnim: AnimState = { frameIndex: 0, elapsed: 0 };

/** Pick the flame sheet for a given mode. */
function flameSheetFor(mode: FlameMode): typeof FLAME_IDLE_SHEET | null {
  switch (mode) {
    case 'IDLE':   return FLAME_IDLE_SHEET;
    case 'CRUISE': return FLAME_CRUISE_SHEET;
    case 'BOOST':  return FLAME_BOOST_SHEET;
    default:       return null;
  }
}

/** Pick the flame HTMLImageElement for a given mode. */
function flameImgFor(
  mode: FlameMode,
  idleImg: HTMLImageElement,
  cruiseImg: HTMLImageElement,
  boostImg: HTMLImageElement,
): HTMLImageElement | null {
  switch (mode) {
    case 'IDLE':   return idleImg;
    case 'CRUISE': return cruiseImg;
    case 'BOOST':  return boostImg;
    default:       return null;
  }
}

/**
 * Whether the ship should rotate around the ramp pivot (parked on ramp)
 * rather than its own centre (free flight during takeoff).
 */
function shipOnRamp(): boolean {
  const b = finaleState.beat;
  return b !== 'NONE' && b !== 'TAKEOFF' && b !== 'OFFSCREEN' && b !== 'FADE_OUT';
}

/**
 * Draw a single flame sprite at the attachment point.
 * Called in the ship's local coordinate system (origin = ship centre).
 */
function drawFlameLayer(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  sheet: typeof FLAME_IDLE_SHEET,
  frameIndex: number,
  alpha: number,
  fw: number,
  fh: number,
): void {
  if (alpha <= 0) return;
  const srcX = frameIndex * sheet.frameWidth;
  // Flame positioned so its right edge = anchor X, vertical centre = anchor Y.
  // Ship is drawn at (-fw/2, -fh/2), so anchor in local coords:
  const anchorLocalX = -fw / 2 + FLAME_ANCHOR.x;
  const anchorLocalY = -fh / 2 + FLAME_ANCHOR.y;
  const flameX = anchorLocalX - sheet.frameWidth;
  const flameY = anchorLocalY - sheet.frameHeight / 2;
  ctx.globalAlpha *= alpha;
  ctx.drawImage(
    img,
    srcX, 0, sheet.frameWidth, sheet.frameHeight,
    flameX, flameY, sheet.frameWidth, sheet.frameHeight,
  );
}

/**
 * Draw the nova_ship sprite with optional flame layer behind it.
 *
 * When on the ramp, rotation pivots at the ramp base so ship and pad
 * tilt as one rigid unit.  During takeoff the ship rotates around its
 * own centre and flies freely.
 */
function drawShip(
  ctx: CanvasRenderingContext2D,
  shipImg: HTMLImageElement,
  flameIdleImg: HTMLImageElement,
  flameCruiseImg: HTMLImageElement,
  flameBoostImg: HTMLImageElement,
  camX: number,
  camY: number,
  dt: number,
): void {
  if (!finaleState.shipVisible) return;
  // DECISION: Ship is visible whenever the beat is active (no clip-gating
  // needed — nova_ship.png is a single static frame, always drawn).
  if (finaleState.beat === 'NONE' || finaleState.beat === 'OFFSCREEN' ||
      finaleState.beat === 'FADE_OUT') return;

  const fw = NOVA_SHIP_SHEET.frameWidth;
  const fh = NOVA_SHIP_SHEET.frameHeight;
  const angle = (finaleState.shipAngle * Math.PI) / 180;

  ctx.save();

  // Fade out during takeoff to prevent canvas-edge clipping artifact.
  if (finaleState.beat === 'TAKEOFF') {
    const fadeProgress = Math.min(finaleState.timer / 0.4, 1);
    ctx.globalAlpha = 1 - fadeProgress;
  }

  if (shipOnRamp()) {
    const pivotX = finaleState.rampX - camX;
    const pivotY = finaleState.rampBaseY - camY;
    ctx.translate(pivotX, pivotY);
    ctx.rotate(-angle);
    const relX = finaleState.shipX - finaleState.rampX + fw / 2;
    const relY = finaleState.shipY - finaleState.rampBaseY + fh / 2;
    ctx.translate(relX, relY);
  } else {
    const sx = finaleState.shipX - camX;
    const sy = finaleState.shipY - camY;
    ctx.translate(sx + fw / 2, sy + fh / 2);
    ctx.rotate(-angle);
  }

  // ── Flame layer (drawn BEHIND the ship) ──────────────────────────────────
  const mode = finaleState.flameMode;
  if (mode !== 'NONE') {
    // Advance the shared flame animation timer.
    const result = advanceAnim(_flameAnim.elapsed, FLAME_CLIP, dt);
    _flameAnim.frameIndex = result.frameIndex;
    _flameAnim.elapsed = result.elapsed;

    const sheet = flameSheetFor(mode);
    const img = flameImgFor(mode, flameIdleImg, flameCruiseImg, flameBoostImg);
    if (sheet && img) {
      const savedAlpha = ctx.globalAlpha;
      drawFlameLayer(ctx, img, sheet, _flameAnim.frameIndex,
        finaleState.flameCrossfade, fw, fh);
      ctx.globalAlpha = savedAlpha;
    }
  }

  // Crystal glow aura.
  if (finaleState.crystalGlow > 0) {
    const glowRadius = Math.max(fw, fh) * 0.7;
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
    grad.addColorStop(0, `rgba(0, 229, 255, ${finaleState.crystalGlow * 0.35})`);
    grad.addColorStop(0.6, `rgba(0, 229, 255, ${finaleState.crystalGlow * 0.12})`);
    grad.addColorStop(1, 'rgba(0, 229, 255, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(-glowRadius, -glowRadius, glowRadius * 2, glowRadius * 2);
  }

  // Ship sprite (single frame, drawn on top of flame).
  ctx.drawImage(shipImg, 0, 0, fw, fh, -fw / 2, -fh / 2, fw, fh);

  ctx.restore();
}

/**
 * Draw the "Press X" action prompt floating above Nova when near the gate.
 *
 * @param ctx     - Canvas 2D context.
 * @param novaX   - Nova's world X position.
 * @param novaY   - Nova's world Y position.
 * @param camX    - Camera X offset.
 * @param camY    - Camera Y offset.
 */
function drawActionPrompt(
  ctx: CanvasRenderingContext2D,
  novaX: number,
  novaY: number,
  camX: number,
  camY: number,
): void {
  if (!finaleState.showActionPrompt) return;

  const px = novaX + 16 - camX;
  // Bob up and down.
  const bob = Math.sin(Date.now() * 0.005) * 3;
  const py = novaY - 20 + bob - camY;

  // Background pill.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(px - 14, py - 8, 28, 16);

  // "X" text.
  ctx.fillStyle = '#00e5ff';
  ctx.font = 'bold 10px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('X', px, py);
}

/**
 * Draw the skip cutscene prompt at bottom-right.
 *
 * @param ctx - Canvas 2D context.
 */
function drawSkipPrompt(ctx: CanvasRenderingContext2D): void {
  if (!finaleState.showSkipPrompt) return;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.font = '9px monospace';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText('Skip ▶', VIEWPORT_WIDTH - 8, VIEWPORT_HEIGHT - 8);
}

/**
 * Draw the full-screen fade-to-black overlay.
 *
 * @param ctx - Canvas 2D context.
 */
function drawFadeOverlay(ctx: CanvasRenderingContext2D): void {
  if (finaleState.fadeAlpha <= 0) return;

  ctx.fillStyle = `rgba(0, 0, 0, ${finaleState.fadeAlpha})`;
  ctx.fillRect(0, 0, VIEWPORT_WIDTH, VIEWPORT_HEIGHT);
}

/**
 * Main entry point — draw all finale elements for the current frame.
 * Called from GameCanvas.web.tsx after normal scene draw, before HUD.
 *
 * @param ctx           - Canvas 2D context.
 * @param shipImg       - Loaded nova_ship.png HTMLImageElement.
 * @param flameIdleImg  - Loaded flame_idle.png HTMLImageElement.
 * @param flameCruiseImg - Loaded flame_cruise.png HTMLImageElement.
 * @param flameBoostImg - Loaded flame_boost.png HTMLImageElement.
 * @param camX          - Camera X offset (integer-rounded).
 * @param camY          - Camera Y offset (integer-rounded).
 * @param dt            - Frame delta in seconds.
 * @param novaX         - Nova's world X (for action prompt positioning).
 * @param novaY         - Nova's world Y (for action prompt positioning).
 */
export function drawFinale(
  ctx: CanvasRenderingContext2D,
  shipImg: HTMLImageElement,
  flameIdleImg: HTMLImageElement,
  flameCruiseImg: HTMLImageElement,
  flameBoostImg: HTMLImageElement,
  camX: number,
  camY: number,
  dt: number,
  novaX: number,
  novaY: number,
): void {
  if (finaleState.beat === 'NONE') return;

  // DECISION: drawGate() removed — the portal sprite (drawExitPortal) now
  // provides the archway visual. Active/inactive state handles open/closed.
  drawRamp(ctx, camX, camY);
  drawShip(ctx, shipImg, flameIdleImg, flameCruiseImg, flameBoostImg, camX, camY, dt);
  drawDust(ctx, camX, camY);
  drawSonicBoom(ctx, camX, camY);
  drawActionPrompt(ctx, novaX, novaY, camX, camY);
  drawSkipPrompt(ctx);
  drawFadeOverlay(ctx);
}
